'use client';

import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import { useConversation } from '@elevenlabs/react';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { ApiGetPhoneNumber, fetcher, isUserGetPhoneNumberOk } from '@utils/data-fetching';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected';

interface HeadlessElevenLabsConversationProps {
  conversationToken: string;
  locale: Locale;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: string) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
  onIdentityLoading?: (loading: boolean) => void;
  onIdentityError?: (error: string | null) => void;
}

/**
 * ElevenLabsConversation
 *
 * Headless conversation engine - no UI, just logic:
 * - Auto-starts on mount
 * - Auto-stops on unmount
 * - Manages WebRTC connection
 * - Provides callbacks for state changes
 * - Properly releases microphone when done
 * - Resolves user identity (phone number, user slug) and sends to ElevenLabs
 */
export const ElevenLabsConversation: React.FC<HeadlessElevenLabsConversationProps> = ({
  conversationToken,
  locale,
  onConnect,
  onDisconnect,
  onError,
  onConnectionStateChange,
  onIdentityLoading,
  onIdentityError,
}) => {
  const [hasStarted, setHasStarted] = useState(false);
  const [loadingIdentity, setLoadingIdentity] = useState(true);
  const [, setIdentityError] = useState<string | null>(null);
  const [dynamicVariables, setDynamicVariables] = useState<Record<string, string> | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const headerInfo = useAppSelector(getUserHeaderInfo);

  const conversation = useConversation({
    serverLocation: 'eu-residency',
    onConnect: () => {
      onConnectionStateChange?.('connected');
      onConnect?.();
    },
    onDisconnect: () => {
      onConnectionStateChange?.('disconnected');
      onDisconnect?.();
    },
    onError: (error: string) => {
      console.error('ElevenLabs conversation error:', error);
      onConnectionStateChange?.('disconnected');
      onError?.(error);
    },
  });

  // Resolve user identity: fetch phone number and user slug
  useEffect(() => {
    let cancelled = false;

    const resolveIdentity = async () => {
      onIdentityLoading?.(true);

      try {
        // Try to get user's phone number
        const phoneRes = await fetcher<ApiGetPhoneNumber>(
          '/v1/user/phone-number',
          locale,
        );
        let phoneNumber: string | null = null;

        if (isUserGetPhoneNumberOk(phoneRes)) {
          phoneNumber = `+${phoneRes.data.country.data.countryCode}${phoneRes.data.number}`;
        }

        if (cancelled) return;

        const userSlug = headerInfo?.userSlug ?? null;
        const vars: Record<string, string> = {};
        if (userSlug) vars.user_slug = userSlug;
        if (phoneNumber) vars.phone_number = phoneNumber;

        if (Object.keys(vars).length > 0) {
          setDynamicVariables(vars);
          setIdentityError(null);
          setLoadingIdentity(false);
          onIdentityLoading?.(false);
          onIdentityError?.(null);
          return;
        }

        // Neither phone nor user_slug available - this is OK, continue without identity
        setDynamicVariables({});
        setIdentityError(null);
        setLoadingIdentity(false);
        onIdentityLoading?.(false);
        onIdentityError?.(null);
      } catch (e) {
        console.error('Failed to resolve ElevenLabs identity variables', e);
        if (!cancelled) {
          // Don't block the conversation if identity resolution fails
          setDynamicVariables({});
          setIdentityError(null);
          setLoadingIdentity(false);
          onIdentityLoading?.(false);
          onIdentityError?.(null);
        }
      }
    };

    void resolveIdentity();

    return () => {
      cancelled = true;
    };
  }, [locale, headerInfo?.userSlug, onIdentityLoading, onIdentityError]);

  // Auto-start conversation after identity is resolved
  const startConversation = useCallback(async () => {
    if (hasStarted) return;
    if (loadingIdentity) return; // Wait for identity resolution
    if (dynamicVariables === null) return; // Wait for dynamicVariables to be set

    setHasStarted(true);
    onConnectionStateChange?.('connecting');

    try {
      // Request microphone permission and store the stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Start the conversation with user identity
      await conversation.startSession({
        conversationToken,
        // Provide user identity to the agent (if available)
        ...(Object.keys(dynamicVariables).length > 0 && { dynamicVariables }),
      });
    } catch (error) {
      console.error('Failed to start ElevenLabs conversation:', error);
      onConnectionStateChange?.('disconnected');
      onError?.(error instanceof Error ? error.message : 'Unknown error');
    }
  }, [
    conversation,
    conversationToken,
    hasStarted,
    loadingIdentity,
    dynamicVariables,
    onConnectionStateChange,
    onError,
  ]);

  useEffect(() => {
    void startConversation();
  }, [startConversation]);

  // Keep ref updated with latest conversation object
  const conversationRef = useRef(conversation);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  // Auto-cleanup on unmount using ref
  useEffect(() => () => {
    // End the ElevenLabs session
    void conversationRef.current.endSession().catch(console.error);

    // Stop all media tracks to release the microphone
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
  }, []);

  // Headless - render nothing
  return null;
};
